import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Trash2, Pencil, Check, X } from "lucide-react";

// 16-colour palette covering the practical range a Greek POS operator
// would pick from for restaurant zones (beach/veranda/passe/etc.). The
// «Custom» swatch reveals a native <input type=color> for any hex.
const PALETTE = [
  "#3B82F6", // blue
  "#0EA5E9", // sky
  "#06B6D4", // cyan
  "#14B8A6", // teal
  "#10B981", // emerald
  "#84CC16", // lime
  "#EAB308", // yellow
  "#F59E0B", // amber
  "#F97316", // orange
  "#EF4444", // red
  "#EC4899", // pink
  "#D946EF", // fuchsia
  "#A855F7", // purple
  "#6366F1", // indigo
  "#64748B", // slate
  "#0F172A", // near-black
];

/**
 * Compact palette + custom-hex picker for restaurant section colours.
 * 8 swatches per row; a clicked swatch updates `value` immediately.
 * The trailing «🎨» swatch toggles a native colour input so the operator
 * can dial in an off-palette hex without leaving the page.
 */
export function ColorPaletteInput({ value, onChange, testId }) {
  const [customOpen, setCustomOpen] = useState(false);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <div
          className="w-7 h-7 rounded-md border-2 border-slate-300 shrink-0"
          style={{ background: value }}
          data-testid={testId ? `${testId}-preview` : undefined}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 w-20 text-xs font-mono px-1.5 border border-slate-300 rounded uppercase"
          maxLength={7}
          data-testid={testId ? `${testId}-hex` : undefined}
        />
        <button
          type="button"
          onClick={() => setCustomOpen((o) => !o)}
          className="h-7 px-2 text-xs rounded border border-slate-300 hover:bg-slate-100"
          title="Παλέτα"
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          🎨
        </button>
      </div>
      {customOpen && (
        <div className="grid grid-cols-8 gap-1 p-2 bg-white border border-slate-200 rounded-md">
          {PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                onChange(c);
                setCustomOpen(false);
              }}
              className={`w-7 h-7 rounded border-2 transition-all hover:scale-110 ${
                value.toUpperCase() === c.toUpperCase()
                  ? "border-slate-900 ring-2 ring-offset-1 ring-slate-400"
                  : "border-slate-200"
              }`}
              style={{ background: c }}
              title={c}
            />
          ))}
          <label
            className="w-7 h-7 rounded border-2 border-dashed border-slate-400 flex items-center justify-center cursor-pointer hover:bg-slate-50"
            title="Προσαρμοσμένο hex"
          >
            <span className="text-[10px] text-slate-500">…</span>
            <input
              type="color"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="sr-only"
            />
          </label>
        </div>
      )}
    </div>
  );
}

/**
 * One row in the «Τμήματα Καταστήματος» list. Inline-editable name with
 * a save/cancel pair, palette-driven colour swap, and a delete button
 * guarded server-side against deleting a section that still has tables.
 */
export function SectionRow({ section, onUpdate, onDelete, tablesCount }) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(section.name);

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded-md border-2"
      style={{
        background: `${section.color}10`,
        borderColor: section.color,
      }}
      data-testid={`section-row-${section.id}`}
    >
      <div
        className="w-3 h-3 rounded-full shrink-0"
        style={{ background: section.color }}
      />
      {editing ? (
        <>
          <Input
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            className="h-7 flex-1"
            autoFocus
            data-testid={`section-row-name-${section.id}`}
          />
          <Button
            size="sm"
            onClick={() => {
              const nm = draftName.trim();
              if (nm && nm !== section.name) onUpdate({ name: nm });
              setEditing(false);
            }}
            className="h-7 w-7 p-0 bg-emerald-600 hover:bg-emerald-700"
            title="Αποθήκευση"
            data-testid={`section-row-save-${section.id}`}
          >
            <Check size={14} />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setDraftName(section.name);
              setEditing(false);
            }}
            className="h-7 w-7 p-0"
            title="Άκυρο"
          >
            <X size={14} />
          </Button>
        </>
      ) : (
        <>
          <div className="flex-1 font-bold text-slate-800 truncate">{section.name}</div>
          <span className="text-[11px] text-slate-500 font-mono">
            {tablesCount} τραπ.
          </span>
          <ColorPaletteInput
            value={section.color}
            onChange={(c) => onUpdate({ color: c })}
            testId={`section-color-${section.id}`}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditing(true)}
            className="h-7 w-7 p-0"
            title="Μετονομασία"
            data-testid={`section-row-edit-${section.id}`}
          >
            <Pencil size={13} />
          </Button>
          <Button
            size="sm"
            onClick={onDelete}
            className="h-7 w-7 p-0 bg-red-600 hover:bg-red-700"
            title="Διαγραφή"
            disabled={tablesCount > 0}
            data-testid={`section-row-delete-${section.id}`}
          >
            <Trash2 size={13} />
          </Button>
        </>
      )}
    </div>
  );
}
